import { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { fetchHostConversations } from '../../lib/hostConversations';
import { ADMIN_GROUPS } from '../../lib/adminNav';
import { AppSidebarShell } from './AppSidebarShell';
import { Avatar } from '../design-system';
import type { SidebarNavItem, SidebarSection } from '../design-system';

// ── Traveler nav (unchanged) ─────────────────────────────────────────────────

interface NavEntry {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
}

const TRAVELER_NAV: NavEntry[] = [
  {
    to: '/home', label: 'Home',
    icon: <path fillRule="evenodd" d="M10 2c-2.236 0-4.43.18-6.57.524C1.993 2.755 1 4.014 1 5.426v5.148c0 1.413.993 2.67 2.43 2.902.848.137 1.705.248 2.57.331v3.443a.75.75 0 001.28.53l3.58-3.579a.78.78 0 01.527-.224 41.202 41.202 0 005.183-.5c1.437-.232 2.43-1.49 2.43-2.903V5.426c0-1.413-.993-2.67-2.43-2.902A41.289 41.289 0 0010 2zm0 7a1 1 0 100-2 1 1 0 000 2zM6 9a1 1 0 11-2 0 1 1 0 012 0zm5 1a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />,
  },
  {
    to: '/matches', label: 'Matches',
    icon: <path d="M4.5 6.375a4.125 4.125 0 118.25 0 4.125 4.125 0 01-8.25 0zM14.25 8.625a3.375 3.375 0 116.75 0 3.375 3.375 0 01-6.75 0zM1.5 19.125a7.125 7.125 0 0114.25 0v.003l-.001.119a.75.75 0 01-.363.63 13.067 13.067 0 01-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 01-.364-.63l-.001-.122zM17.25 19.128l-.001.144a2.25 2.25 0 01-.233.96 10.088 10.088 0 005.06-1.01.75.75 0 00.42-.643 4.875 4.875 0 00-6.957-4.611 8.586 8.586 0 011.71 5.157v.003z" />,
  },
];

// ── Host nav — per the Host Trips Kanban / Host Chats prototypes ─────────────
// "Your work": Active trips, Chats (unread badge), Past trips
// "Manage":    My profile, Event library, Availability, Earnings
//
// My profile is a real nav item because the identity card that used to be its
// only entry point is hidden on the collapsed desktop rail — leaving hosts with
// no route to the screen that gates both matching and payment.
// The host profile card (headerSlot) links to the profile editor.

interface HostNavEntry {
  to: string;
  label: string;
  /** Tabler icon name. */
  icon: string;
  end?: boolean;
}

const HOST_WORK_NAV: HostNavEntry[] = [
  { to: '/host', label: 'Active trips', icon: 'layout-kanban', end: true },
  { to: '/host/chats', label: 'Chats', icon: 'message-circle' },
  { to: '/host/past', label: 'Past trips', icon: 'archive' },
];

const HOST_MANAGE_NAV: HostNavEntry[] = [
  { to: '/host/profile', label: 'My profile', icon: 'user-circle' },
  { to: '/host/library', label: 'Event library', icon: 'books' },
  { to: '/host/availability', label: 'Availability', icon: 'calendar' },
  { to: '/host/earnings', label: 'Earnings', icon: 'coin' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function Icon({ d }: { d: ReactNode }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0">
      {d}
    </svg>
  );
}

/** Host identity card under the logo — avatar, name, "Host · City". */
function HostProfileCard({ onNavigate }: { onNavigate: () => void }) {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [city, setCity] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    void supabase
      .from('host_profiles')
      .select('city')
      .eq('profile_id', user.id)
      .maybeSingle()
      .then(({ data }) => setCity((data as { city: string | null } | null)?.city ?? null));
  }, [user]);

  return (
    <button
      type="button"
      onClick={() => { navigate('/host/profile'); onNavigate(); }}
      className="w-full text-left mb-5"
      style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 10, padding: '11px 13px', cursor: 'pointer' }}
    >
      <div className="flex items-center gap-2.5">
        <Avatar name={profile?.full_name ?? 'Host'} size={36} src={profile?.avatar_url ?? undefined} />
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-app-fg truncate">{profile?.full_name ?? 'Host'}</div>
          <div className="text-[10.5px] text-app-muted mt-[3px] truncate">
            Host{city ? ` · ${city}` : ''}
          </div>
        </div>
      </div>
    </button>
  );
}

// ── Main sidebar ─────────────────────────────────────────────────────────────

const COLLAPSE_KEY = 'sk_sidebar_collapsed';

interface AppSidebarProps {
  open: boolean;
  onClose: () => void;
}

export function AppSidebar({ open, onClose }: AppSidebarProps) {
  const { user, activeRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });

  function toggleCollapsed() {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }

  // Unread chat count for the host "Chats" badge.
  useEffect(() => {
    if (!user || activeRole !== 'host') return;
    void fetchHostConversations(user.id).then((rows) => {
      setUnreadCount(rows.filter((r) => r.unread).length);
    });
  }, [user, activeRole, location.pathname]);

  // Trips with no host left: the admin's "Host Matching" badge.
  const [unassignedCount, setUnassignedCount] = useState(0);
  useEffect(() => {
    if (!user || activeRole !== 'admin') return;
    void supabase
      .from('matches')
      .select('id', { count: 'exact', head: true })
      .eq('needs_host', true)
      .then(({ count }) => setUnassignedCount(count ?? 0));
  }, [user, activeRole, location.pathname]);

  const isActive = (to: string, end?: boolean) =>
    end ? location.pathname === to : location.pathname === to || location.pathname.startsWith(`${to}/`);

  const toItem = (entry: NavEntry): SidebarNavItem => ({
    key: entry.to,
    icon: <Icon d={entry.icon} />,
    label: entry.label,
    badge: entry.to === '/admin/host-matching' && unassignedCount > 0 ? unassignedCount : undefined,
    onClick: () => { navigate(entry.to); onClose(); },
  });

  const toHostItem = (entry: HostNavEntry): SidebarNavItem => ({
    key: entry.to,
    icon: entry.icon,
    label: entry.label,
    badge: entry.to === '/host/chats' && unreadCount > 0 ? unreadCount : undefined,
    onClick: () => { navigate(entry.to); onClose(); },
  });

  let sections: SidebarSection[] = [];
  let activeKey: string | undefined;
  let headerSlot: ReactNode;

  if (activeRole === 'traveler') {
    sections = [{ label: 'Your trip', items: TRAVELER_NAV.map(toItem) }];
    activeKey = TRAVELER_NAV.find((entry) => isActive(entry.to, entry.end))?.to;
  } else if (activeRole === 'host') {
    sections = [
      { label: 'Your work', items: HOST_WORK_NAV.map(toHostItem) },
      { label: 'Manage', items: HOST_MANAGE_NAV.map(toHostItem) },
    ];
    const all = [...HOST_WORK_NAV, ...HOST_MANAGE_NAV];
    // /host/profile now has its own nav item under Manage; the identity card
    // links there too.
    activeKey = all.find((entry) => isActive(entry.to, entry.end))?.to;
    // Hidden on the collapsed desktop rail; the mobile drawer always shows it.
    headerSlot = (
      <div className={collapsed ? 'md:hidden' : undefined}>
        <HostProfileCard onNavigate={onClose} />
      </div>
    );
  } else if (activeRole === 'admin') {
    sections = ADMIN_GROUPS.map((group) => ({
      label: group.label,
      collapsible: true,
      items: group.items.map((item) => toItem({ to: item.to, label: item.label, icon: item.icon })),
    }));
    activeKey = ADMIN_GROUPS.flatMap((g) => g.items).find((item) => isActive(item.to))?.to;
  }

  const logoHref = activeRole === 'host' ? '/host' : activeRole === 'admin' ? '/admin' : '/home';

  return (
    <AppSidebarShell
      open={open}
      onClose={onClose}
      logoHref={logoHref}
      collapsed={collapsed}
      onToggleCollapse={toggleCollapsed}
      sections={sections}
      active={activeKey}
      headerSlot={headerSlot}
    />
  );
}
